import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import Seo from "@/components/site/Seo";
import { trackEvent } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      axios.get(`${API}/case-studies/${slug}`),
      axios.get(`${API}/case-studies`),
    ])
      .then(([detail, list]) => {
        setItem(detail.data);
        setRelated(list.data.filter((x) => x.slug !== slug).slice(0, 2));
        trackEvent("case_study_read", { slug });
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-32 text-[#6B7280]">Loading…</div>
    );
  }

  if (error || !item) {
    return (
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-32">
        <h1 className="font-display text-4xl font-bold text-[#07111F]">Case study not found.</h1>
        <p className="mt-4 text-[#6B7280]">It may have been moved or renamed.</p>
        <Link to="/case-studies" className="mt-8 inline-flex items-center gap-2 text-[#6D4DFF] hover:gap-3 transition-all">
          <ArrowLeft className="w-4 h-4" /> Back to case studies
        </Link>
      </section>
    );
  }

  return (
    <>
      <Seo
        title={`${item.headline} | Xadentra Case Study`}
        description={item.challenge}
        path={`/case-studies/${item.slug}`}
      />

      {/* HEADER */}
      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 480, height: 480, background: "#6D4DFF", top: -180, left: -100, opacity: 0.5 }} />
        <div className="xa-blob" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -120, right: -120, opacity: 0.3 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24 relative">
          <Link
            to="/case-studies"
            data-testid="case-study-back"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4" /> All playbooks
          </Link>
          <div className="mt-8 font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">
            {item.industry} · {item.client}
          </div>
          <h1 className="font-display mt-5 text-4xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.02] max-w-5xl">
            {item.headline}
          </h1>
          <p className="mt-6 text-base lg:text-lg text-white/65 max-w-3xl leading-relaxed">
            Anonymized engagement summary. Structured as Problem · What we fixed · Tools · Outcome · Why it mattered.
          </p>
        </div>
      </section>

      {/* OVERVIEW STRIP */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { l: "Industry", v: item.industry },
            { l: "Duration", v: item.duration || "—" },
            { l: "Team", v: item.team || "—" },
            { l: "Services", v: (item.services || []).join(" · ") },
          ].map((s, i) => (
            <div key={i}>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">{s.l}</div>
              <div className="mt-2 text-sm text-[#07111F] leading-snug">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BODY */}
      <section className="bg-[#F7F9FC] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <article className="lg:col-span-8 space-y-12" data-testid="case-study-article">
            {/* Snapshot — Problem / What we fixed / Tools / Outcome / Why it mattered */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-8 lg:p-10 space-y-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">Snapshot</div>
              <Row label="Problem" body={item.problem || item.challenge} />
              <Row label="What we fixed" body={item.what_we_fixed || item.solution} />
              <Row label="Tools" body={(item.tools && item.tools.join(", ")) || (item.stack && item.stack.join(", ")) || "—"} />
              <Row label="Outcome" body={item.outcome || item.impact} />
              {item.why_it_mattered && <Row label="Why it mattered" body={item.why_it_mattered} />}
            </div>

            {(item.sections || []).map((s, i) => (
              <div key={i} data-testid={`case-section-${i}`}>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h2 className="font-display mt-3 text-2xl lg:text-3xl font-bold tracking-tight text-[#07111F]">
                  {s.heading}
                </h2>
                <p className="mt-5 text-[#374151] leading-[1.8] text-base lg:text-lg">{s.text}</p>
              </div>
            ))}
          </article>

          {/* SIDEBAR */}
          <aside className="lg:col-span-4 space-y-8 lg:sticky lg:top-24 h-fit">
            {(item.tools?.length > 0 || item.stack?.length > 0) && (
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-7">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">Tools involved</div>
                <ul className="mt-4 space-y-2.5">
                  {(item.tools || item.stack || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[#07111F]">
                      <CheckCircle2 className="w-4 h-4 text-[#3EE8A5] mt-0.5 shrink-0" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-[#07111F] text-white rounded-2xl p-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">Similar setup?</div>
              <h3 className="font-display mt-3 text-xl font-semibold">
                Talk to a senior specialist about yours.
              </h3>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">
                30 minutes, focused on your Marketo, Salesforce, lifecycle, or reporting question.
              </p>
              <Link
                to="/contact#booking-section"
                data-testid="case-study-cta"
                className="mt-6 xa-btn xa-btn-primary w-full justify-center"
              >
                Book a meeting <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="bg-white border-b border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">More work</div>
            <h2 className="font-display mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-[#07111F]">
              Related case studies.
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {related.map((c, i) => (
                <Link
                  to={`/case-studies/${c.slug}`}
                  key={c.slug}
                  data-testid={`case-related-${i}`}
                  className="xa-lift bg-white border border-[#E5E7EB] rounded-2xl p-8 hover:border-[#6D4DFF]/40 group block"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">{c.industry}</div>
                  <h3 className="font-display mt-3 text-xl font-semibold text-[#07111F] group-hover:text-[#6D4DFF] transition-colors">
                    {c.headline}
                  </h3>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#6D4DFF]">
                    Read case study <ArrowUpRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

function Row({ label, body }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-5">
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#6B7280] sm:pt-1">
        {label}
      </div>
      <p className="sm:col-span-3 text-[#07111F] leading-relaxed text-sm">{body}</p>
    </div>
  );
}
